/**
 * Resolve financial metric series from statement JSON, API ratios, or formulas.
 *
 * Never mutates raw financial objects. Display transforms (absolute value for
 * coverage / capex comparisons) are applied to copied series only.
 */

import { FINANCIAL_METRICS, getFinancialMetric } from '../config/financialMetricRegistry';
import {
    absoluteValue,
    isPeriodKey,
    normalizePeriodKey,
    parseMetricValue,
    safeDivide,
    safeSum,
} from './financialValueParse';
import { parseUnitForMetricValue } from './financialUnit';
import { formatRatioYears } from './yearFormat';

const STATEMENT_KEYS = ['profitAndLoss', 'balanceSheet', 'cashFlow'];
const BASIS_KEYS = ['consolidated', 'standalone'];

function isPlainObject(value) {
    return value != null && typeof value === 'object' && !Array.isArray(value);
}

function looksLikeStatementBundle(value) {
    return isPlainObject(value) && STATEMENT_KEYS.some((key) => isPlainObject(value[key]));
}

function looksLikeBasisBundle(value) {
    return isPlainObject(value) && BASIS_KEYS.some((key) => looksLikeStatementBundle(value[key]));
}

function pickBasis(bundle, preferredBasis) {
    if (!isPlainObject(bundle)) return null;
    return bundle[preferredBasis]
        || bundle.consolidated
        || bundle.standalone
        || null;
}

/**
 * Locate the P&L / BS / CF bundle inside a company document or a pre-sliced payload.
 *
 * Supports:
 * - full company document: data.financials.consolidated.cashFlow
 * - already-unwrapped financials: data.consolidated.cashFlow
 * - already-selected statements: data.cashFlow
 * - a bare statement object (line-item keys) when sourcePath is applied later
 *
 * @param {object} data
 * @param {string} [preferredBasis='consolidated']
 * @returns {object|null}
 */
export function getFinancialStatementRoot(data, preferredBasis = 'consolidated') {
    if (!isPlainObject(data)) return null;

    const payload = isPlainObject(data.data) ? data.data : data;

    if (looksLikeStatementBundle(payload)) return payload;

    if (looksLikeBasisBundle(payload)) {
        return pickBasis(payload, preferredBasis);
    }

    if (looksLikeStatementBundle(payload.financials)) {
        return payload.financials;
    }

    if (looksLikeBasisBundle(payload.financials)) {
        return pickBasis(payload.financials, preferredBasis);
    }

    return isPlainObject(payload.financials) ? payload.financials : payload;
}

function readByPath(root, sourcePath) {
    if (!root || !Array.isArray(sourcePath) || sourcePath.length === 0) return null;
    let cursor = root;
    for (const segment of sourcePath) {
        if (!isPlainObject(cursor)) return null;
        if (Object.prototype.hasOwnProperty.call(cursor, segment)) {
            cursor = cursor[segment];
            continue;
        }
        // Already-selected statement: skip the statement key if the cursor is the line map.
        if (STATEMENT_KEYS.includes(segment) && !looksLikeStatementBundle(cursor)) {
            continue;
        }
        return null;
    }
    return cursor;
}

function seriesFromYearMap(yearMap, unit) {
    if (!isPlainObject(yearMap)) return [];
    return Object.entries(yearMap)
        .filter(([key]) => isPeriodKey(key))
        .map(([key, raw]) => {
            const year = normalizePeriodKey(key);
            return year ? { year, value: parseMetricValue(raw, unit) } : null;
        })
        .filter(Boolean)
        .sort(comparePeriodPoints);
}

function comparePeriodPoints(a, b) {
    if (a.year === 'TTM') return 1;
    if (b.year === 'TTM') return -1;
    return Number(a.year) - Number(b.year);
}

function hasNumericPoint(point) {
    return point != null && point.value != null && Number.isFinite(point.value);
}

/**
 * Extract a direct statement series. Missing lines return [] rather than throwing.
 *
 * @param {object} root
 * @param {string[]} sourcePath
 * @param {string} [unit='currency']
 * @returns {{ year: string, value: number|null }[]}
 */
export function extractDirectSeries(root, sourcePath, unit = 'currency') {
    const yearMap = readByPath(root, sourcePath);
    if (!isPlainObject(yearMap)) return [];
    return seriesFromYearMap(yearMap, unit);
}

function seriesFromApiRatio(allRatios, ratioName, unit) {
    if (!Array.isArray(allRatios) || !ratioName) return [];
    const match = allRatios.find((ratio) => ratio && ratio.name === ratioName);
    if (!match) return [];
    const values = Array.isArray(match.values) ? match.values : [];
    return values
        .map((point) => {
            const year = normalizePeriodKey(point?.year);
            if (!year) return null;
            const raw = point && Object.prototype.hasOwnProperty.call(point, 'value')
                ? point.value
                : point;
            return { year, value: parseMetricValue(raw, unit) };
        })
        .filter(Boolean)
        .sort(comparePeriodPoints);
}

function toYearMap(series) {
    const map = new Map();
    (series || []).forEach((point) => {
        if (!point || !point.year) return;
        map.set(String(point.year), point.value);
    });
    return map;
}

function unionYears(seriesList) {
    const years = new Set();
    seriesList.forEach((series) => {
        (series || []).forEach((point) => {
            if (point && point.year) years.add(String(point.year));
        });
    });
    return Array.from(years)
        .map((year) => ({ year, value: null }))
        .sort(comparePeriodPoints)
        .map((point) => point.year);
}

function computeAtYear(kind, inputsAtYear) {
    const [a, b] = inputsAtYear;
    switch (kind) {
        case 'sum':
            return safeSum(...inputsAtYear);
        case 'subtract':
            if (a == null || b == null) return null;
            return safeSum(a, typeof b === 'number' ? -b : b);
        case 'absMinus':
            if (a == null || b == null) return null;
            return safeSum(absoluteValue(a), typeof b === 'number' ? -b : b);
        case 'ratio':
            return safeDivide(a, b);
        case 'ratioAbsNumerator':
            return safeDivide(absoluteValue(a), b);
        case 'ratioAbsDenominator':
            return safeDivide(a, absoluteValue(b));
        case 'ratioPercent': {
            const ratio = safeDivide(a, b);
            return ratio == null ? null : ratio * 100;
        }
        case 'ratioAbsNumeratorPercent': {
            const ratio = safeDivide(absoluteValue(a), b);
            return ratio == null ? null : ratio * 100;
        }
        case 'ratioPositiveDenominator':
            if (b == null || !Number.isFinite(b) || b <= 0) return null;
            return safeDivide(a, b);
        default:
            return null;
    }
}

function computeSeriesFromInputs(kind, inputSeriesList) {
    const years = unionYears(inputSeriesList);
    const maps = inputSeriesList.map(toYearMap);
    return years.map((year) => {
        const values = maps.map((map) => (map.has(year) ? map.get(year) : null));
        // Mismatched fiscal period: skip the ratio when any required input is absent.
        if (values.some((value) => value == null)) {
            return { year, value: null };
        }
        return { year, value: computeAtYear(kind, values) };
    });
}

function resolveCalculatedSeries(metric, context, stack) {
    const inputs = Array.isArray(metric.inputs) ? metric.inputs : [];
    const inputSeries = inputs.map((inputId) => resolveMetricSeries({
        ...context,
        metricId: inputId,
        _stack: stack,
    }));
    return computeSeriesFromInputs(metric.compute, inputSeries);
}

/**
 * Resolve one metric to a year/value series.
 *
 * Direct metrics read sourcePath. If a direct metric has a fallback (reportedFcf)
 * and the source line is empty, the fallback formula is used.
 * Calculated metrics compose other IDs. apiRatio metrics read allRatios.
 *
 * @param {object} params
 * @param {object} [params.data]
 * @param {string} params.metricId
 * @param {Array} [params.allRatios]
 * @param {string} [params.preferredBasis]
 * @returns {{ year: string, value: number|null, metricId: string, label: string, unit: string, type: string }[]}
 */
export function resolveMetricSeries({
    data,
    metricId,
    allRatios = [],
    preferredBasis = 'consolidated',
    _stack = [],
} = {}) {
    const metric = getFinancialMetric(metricId);
    if (!metric) return [];
    if (_stack.includes(metricId)) return [];

    const nextStack = _stack.concat(metricId);
    const root = getFinancialStatementRoot(data, preferredBasis);
    const annotate = (series) => (series || []).map((point) => ({
        ...point,
        metricId: metric.id,
        label: metric.shortLabel || metric.label,
        unit: metric.unit,
        type: metric.type,
    }));

    if (metric.id === 'cfoToEbitda') {
        return annotate(resolveCfoToEbitdaPreferredSeries({
            data,
            allRatios,
            preferredBasis,
            _stack: nextStack,
        }));
    }

    if (metric.type === 'apiRatio') {
        return annotate(seriesFromApiRatio(allRatios, metric.ratioName, metric.unit));
    }

    if (metric.type === 'calculated') {
        return annotate(resolveCalculatedSeries(metric, {
            data,
            allRatios,
            preferredBasis,
        }, nextStack));
    }

    const direct = extractDirectSeries(root, metric.sourcePath, metric.unit);
    const hasAnyValue = direct.some(hasNumericPoint);
    if (hasAnyValue || !metric.fallback) {
        return annotate(direct);
    }

    const fallbackSeries = computeSeriesFromInputs(
        metric.fallback.compute,
        (metric.fallback.inputs || []).map((inputId) => resolveMetricSeries({
            data,
            metricId: inputId,
            allRatios,
            preferredBasis,
            _stack: nextStack,
        })),
    );
    return annotate(fallbackSeries);
}

/**
 * Align series on a shared chronological year axis. Missing points stay null.
 *
 * @param {Array<Array<{ year: string, value: number|null }>>} seriesList
 * @returns {{ years: string[], series: Array<Array<number|null>> }}
 */
export function alignMetricSeries(seriesList) {
    const years = unionYears(seriesList);
    const maps = (seriesList || []).map(toYearMap);
    return {
        years,
        series: maps.map((map) => years.map((year) => (map.has(year) ? map.get(year) : null))),
    };
}

/**
 * Apply a display-only transform. Source series objects are not mutated.
 *
 * @param {Array<{ year: string, value: number|null }>} series
 * @param {string} [transform]
 * @returns {Array<{ year: string, value: number|null }>}
 */
export function applyDisplayTransform(series, transform) {
    if (!transform || !Array.isArray(series)) return series || [];
    if (transform === 'absoluteValue' || transform === 'absoluteValueForCapexComparison') {
        return series.map((point) => ({
            ...point,
            value: absoluteValue(point ? point.value : null),
        }));
    }
    return series.map((point) => ({ ...point }));
}

/**
 * Drop faux TTM zeros when prior non-TTM points are non-zero.
 * Matches ChartModal / CompanyDetail isTTMMissing behavior.
 *
 * @param {Array<{ year: string, value: number|null, displayYear?: string }>} series
 * @returns {Array<{ year: string, value: number|null }>}
 */
export function dropInvalidTtmPoints(series) {
    if (!Array.isArray(series) || series.length === 0) return [];
    const hasPriorNonZero = series
        .filter((point) => point && String(point.year) !== 'TTM' && !String(point.displayYear || '').includes('TTM'))
        .some((point) => point.value != null && point.value !== 0);
    return series.filter((point) => {
        if (!point) return false;
        const isTtm = String(point.year) === 'TTM' || String(point.displayYear || '').includes('TTM');
        if (!isTtm) return true;
        const emptyLike = point.value == null || point.value === 0;
        if (hasPriorNonZero && emptyLike) return false;
        return true;
    });
}

function formatSeriesYears(series, limit = 11) {
    const formatted = formatRatioYears(
        (series || []).map((point) => ({
            year: point.year,
            value: point.value,
            metricId: point.metricId,
            label: point.label,
            unit: point.unit,
            type: point.type,
        })),
        limit,
    );
    return dropInvalidTtmPoints(formatted);
}

/**
 * Resolve every metric in a comparison, apply per-metric display transforms,
 * optionally append a derived metric, and align years.
 *
 * @param {object} params
 * @param {object} [params.data]
 * @param {object} params.comparison
 * @param {Array} [params.allRatios]
 * @param {string} [params.preferredBasis]
 * @param {string[]} [params.extraMetricIds]
 * @returns {{
 *   years: string[],
 *   displayYears: string[],
 *   series: Array<{ metricId: string, label: string, unit: string, type: string, values: Array<number|null>, role: string, stack?: string }>,
 *   empty: boolean
 * }}
 */
export function resolveComparisonChart({
    data,
    comparison,
    allRatios = [],
    preferredBasis = 'consolidated',
    extraMetricIds = [],
} = {}) {
    if (!comparison) {
        return { years: [], displayYears: [], series: [], empty: true };
    }

    const metricIds = Array.from(new Set([
        ...(comparison.metricIds || []),
        ...(extraMetricIds || []),
        ...(comparison.derivedMetricId ? [comparison.derivedMetricId] : []),
    ]));

    const totalIds = new Set(comparison.totalMetricIds || []);
    const derivedId = comparison.derivedMetricId || null;
    const transforms = comparison.displayTransforms || {};

    const resolved = metricIds.map((metricId) => {
        const metric = getFinancialMetric(metricId) || {
            id: metricId,
            label: metricId,
            unit: 'currency',
            type: 'direct',
        };
        const raw = resolveMetricSeries({
            data,
            metricId,
            allRatios,
            preferredBasis,
        });
        const transformed = applyDisplayTransform(raw, transforms[metricId]);
        const formatted = formatSeriesYears(transformed);
        const role = derivedId === metricId
            ? 'derived'
            : (totalIds.has(metricId) ? 'total' : 'primary');
        return {
            metricId,
            label: metric.shortLabel || metric.label || metricId,
            unit: metric.unit || 'currency',
            type: metric.type || 'direct',
            role,
            stack: role === 'primary' && comparison.stackGroup ? comparison.stackGroup : undefined,
            points: formatted,
        };
    });

    const aligned = alignMetricSeries(resolved.map((item) => item.points));
    const displayYearByYear = new Map();
    resolved.forEach((item) => {
        item.points.forEach((point) => {
            if (point && point.year && point.displayYear) {
                displayYearByYear.set(point.year, point.displayYear);
            }
        });
    });

    const series = resolved.map((item, index) => ({
        metricId: item.metricId,
        label: item.label,
        unit: item.unit,
        type: item.type,
        role: item.role,
        stack: item.stack,
        values: aligned.series[index],
    }));

    const empty = series.length === 0
        || series.every((item) => !item.values.some((value) => value != null && Number.isFinite(value)));

    return {
        years: aligned.years,
        displayYears: aligned.years.map((year) => displayYearByYear.get(year) || (year === 'TTM' ? 'TTM' : `Mar${year}`)),
        series,
        empty,
    };
}

/**
 * Convert an opened ratio object into a single formatted series (legacy path).
 *
 * @param {object} ratio
 * @returns {{ years: string[], displayYears: string[], values: Array<number|null>, label: string, unit: string }}
 */
export function resolveOpenedRatioSeries(ratio) {
    const raw = Array.isArray(ratio?.values) ? ratio.values : [];
    const series = raw
        .map((point) => {
            const year = normalizePeriodKey(point?.year);
            if (!year) return null;
            return {
                year,
                value: parseMetricValue(point?.value, parseUnitForMetricValue(ratio?.unit)),
            };
        })
        .filter(Boolean);
    const formatted = formatSeriesYears(series);
    return {
        years: formatted.map((point) => point.year),
        displayYears: formatted.map((point) => point.displayYear || point.year),
        values: formatted.map((point) => point.value),
        label: ratio?.displayName || ratio?.name || 'Series',
        unit: ratio?.unit || '',
        name: ratio?.name || '',
    };
}

export { FINANCIAL_METRICS };

const STATEMENT_DERIVED_RATIO_METRIC_IDS = Object.freeze({
    debtRepaymentToCfo: 'debtRepaymentToCfo',
    fcfAbsolute: 'reportedFcf',
    fcfMargin: 'fcfMargin',
    fcfToNetProfit: 'fcfToNetProfit',
    cfoToEbit: 'cfoToEbit',
    cfoToEbitda: 'cfoToEbitda',
});

function percentPointsToMultiple(value) {
    if (value == null || !Number.isFinite(value)) return null;
    return value / 100;
}

/**
 * Statement CFO/OP is percentage points (Screener). CFO / EBITDA is the
 * same Indian identity as a multiple: prefer the scraped CFO/OP line so
 * both tables convert 87% ↔ 0.87x; otherwise CFO / Operating Profit.
 */
function resolveCfoToEbitdaPreferredSeries({ data, allRatios, preferredBasis, _stack }) {
    const reported = resolveMetricSeries({
        data,
        metricId: 'reportedCfoOp',
        allRatios,
        preferredBasis,
        _stack,
    });
    const computed = resolveCalculatedSeries(getFinancialMetric('cfoToEbitda'), {
        data,
        allRatios,
        preferredBasis,
    }, _stack || []);
    const reportedMap = toYearMap(reported);
    const computedMap = toYearMap(computed);
    return unionYears([reported, computed]).map((year) => {
        if (reportedMap.has(year) && reportedMap.get(year) != null) {
            return { year, value: percentPointsToMultiple(reportedMap.get(year)) };
        }
        return { year, value: computedMap.has(year) ? computedMap.get(year) : null };
    });
}

/**
 * Replace cached API ratio cells with statement-derived values when the
 * registry already knows how to compute them.
 *
 * Free Cash Flow uses Screener net-capex FCF. CFO / EBITDA uses statement
 * CFO/OP (as a multiple) so it converts 1:1 with the raw statement row.
 *
 * @param {Array} ratios
 * @param {{ data?: object, preferredBasis?: string, allRatios?: Array }} [options]
 * @returns {Array}
 */
export function overlayStatementDerivedRatioValues(ratios, {
    data,
    preferredBasis = 'consolidated',
    allRatios = [],
} = {}) {
    if (!Array.isArray(ratios) || !data) return Array.isArray(ratios) ? ratios : [];
    return ratios.map((ratio) => {
        const name = ratio?.name;
        if (!STATEMENT_DERIVED_RATIO_METRIC_IDS[name]) return ratio;
        const series = resolveMetricSeries({
            data,
            metricId: STATEMENT_DERIVED_RATIO_METRIC_IDS[name],
            allRatios,
            preferredBasis,
        });
        if (!series.length) return ratio;
        const byYear = new Map(series.map((point) => [String(point.year), point.value]));
        const values = (ratio.values || []).map((point) => {
            const year = normalizePeriodKey(point?.year) || (point?.year != null ? String(point.year) : '');
            if (!year || !byYear.has(year)) return point;
            const value = byYear.get(year);
            const rounded = value == null || !Number.isFinite(value) ? null : Number(value.toFixed(2));
            return { ...point, value: rounded };
        });
        return { ...ratio, values };
    });
}
